export type SamplePrompt = {
  n: number;
  title: string;
  text: string;
  tips: string[];
};

export const aiTargets = [
  { name: 'ChatGPT', url: (q: string) => `https://chatgpt.com/?q=${encodeURIComponent(q)}` },
  { name: 'Claude', url: (q: string) => `https://claude.ai/new?q=${encodeURIComponent(q)}` },
  { name: 'Gemini', url: () => 'https://gemini.google.com/app' },
];

export const demoOutput = {
  title: 'INCIDENT REPORT — Housing Unit',
  sections: [
    { heading: 'Header', lines: ['Facility, unit, date, time, reporting officer and incident type'] },
    { heading: 'Chronological narrative', lines: ['First person, past tense, in strict time order', 'Only what you saw, heard and did · reported facts identified'] },
    { heading: 'Notifications & outcome', lines: ['Who was notified and when · medical · housing · evidence'] },
  ],
};

// Real prompts copied word for word from Volume 1 of the toolkit.
export const samplePrompts: SamplePrompt[] = [
  {
    n: 1,
    title: 'General Incident Report Narrative',
    text: `Act as a senior corrections report writer with 20 years of experience preparing incident reports that are reviewed by shift supervisors, investigators and administrative hearing officers. Your goal is to turn my raw notes into a professional, objective and chronological incident report.

Open the report with the header: [Facility Name], [Housing Unit or Area], [Date], [Time the Incident Began], [Reporting Officer Name and Badge/ID Number] and [Incident Type].

Write the narrative in first person, past tense, in strict chronological order, using only these facts: [Paste My Raw Notes]. Describe what I personally saw, heard and did. Separate clearly what I observed directly from what was reported to me by others, and identify who reported it: [Information Reported by Others].

Include the people involved by role and identifier as my policy requires: [Inmates Involved], [Staff Involved], [Witnesses]. Document every notification I made and the time: [Supervisor / Medical / Control Notified and Times].

Close with the current status of the incident and the inmates involved: [Outcome, Housing Changes, Medical Treatment, Evidence Collected].

Do not add facts, motives, quotes or times that I did not provide. Replace opinions and labels such as "aggressive" or "combative" with the specific behavior I described. If key information is missing to complete the bracketed fields, ask me the necessary questions before responding.`,
    tips: ['Paste your notes exactly as you wrote them, even if they are messy. The AI does the organizing; you keep the facts.'],
  },
  {
    n: 5,
    title: 'Refusal of a Direct Order Report',
    text: `Act as a corrections disciplinary documentation expert. Write a clear report showing that a lawful order was given, understood and refused, so the report can support a rule-violation charge.

Header: [Facility], [Unit/Location], [Date], [Time], [Reporting Officer and Badge/ID], [Inmate Identifier as Required by Policy].

State the order exactly as given: [Exact Order], the reason for the order: [Operational Reason, e.g., Count, Lockdown, Search, Movement], and how it was given: [Verbal / Written / Intercom], including how many times and at what intervals: [Number of Times and Timing].

Document the facts showing the inmate heard and understood the order: [Inmate's Response, Eye Contact, Statements, Language Used]. Describe the inmate's response to each order as observable behavior: [Response to Each Order].

Document the alternatives and de-escalation I attempted: [Explanations Given, Time Allowed, Supervisor Involvement], and the final outcome: [Complied Later / Refused / Escorted / Planned Use of Force]. Record the rule I believe was violated using the rule number from my facility's handbook: [Rule Number and Title].

Use neutral language and avoid words like "defiant" or "disrespectful" unless followed by the specific behavior. If key information is missing to complete the bracketed fields, ask me the necessary questions before responding.`,
    tips: ['A hearing officer looks for three things: the order was lawful, it was clearly communicated, and it was refused. Make all three obvious.'],
  },
  {
    n: 14,
    title: 'Chronological Timeline Builder',
    text: `Act as a corrections incident analyst. Build a precise chronological timeline from the different sources I give you, so I can write a report where every time is consistent.

Sources: My handwritten or typed notes: [Paste Notes]. Radio or control log times: [Paste Times]. Logbook entries: [Paste Entries]. Camera or video timestamps if reviewed: [Paste Timestamps]. Other staff times if provided to me: [Paste Times].

Create a table with these columns: Time, Event, Source, and Notes. Sort all events in order. When two sources show different times for the same event, list both, mark the line with "TIME CONFLICT – VERIFY", and do not choose one yourself.

After the table, list: events that have no time recorded, gaps longer than [Number] minutes with no recorded activity, and questions I should answer before writing the final report.

Do not invent or estimate times. If key information is missing to complete the bracketed fields, ask me the necessary questions before responding.`,
    tips: ['Make sure your watch, the unit clock and the camera clock agree. Small time differences cause big questions later.'],
  },
];
